import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import _map from 'lodash/map'
import _forEach from 'lodash/forEach'
import _groupBy from 'lodash/groupBy'

import { get_event, get_checkin } from '../../../_common/core/selectors'
import { FETCH_EVENT_CHECKIN_TICKETS } from '../../../_common/redux/performance/actions'
import EmptyBar from '../../_library/EmptyBar'
import SortableTable from '../../_library/SortableTable'
import { getTableColumns } from '../../utils/sortableTableUtils'
import { DAY_DISPLAY_FORMAT } from '../../constants/timeFormats'
import {
  formatDate,
  createFixedFloatNormalizer,
  currencyNormalizerCreator,
} from '../../../_common/core/validation'
import axios from 'axios'
import { showAxiosError } from '../../utils/messenger'
import LoadingBar from '../../_library/LoadingBar'

const CashlessPayments = ({ event, FETCH_EVENT_CHECKIN_TICKETS, isLoading }) => {
  const [tickets, setTickets] = useState([])
  const [ticketDetailsRows, setTicketDetailsRows] = useState([])
  const [sortBy, setSortBy] = useState({ column: 'order_id', asc: false })

  const sortByFn = column => {
    setSortBy({ sortBy: { column: column.key, asc: !sortBy.asc } })
  }

  const ticketsTableColumns = getTableColumns([
    {
      label: 'Ticket ID',
      key: 'qr_data',
      sort: sortByFn,
    },
    {
      label: 'Order ID',
      key: 'order_id',
      sort: sortByFn,
    },
    {
      label: 'Tag ID',
      key: 'credential_id',
      sort: sortByFn,
    },
    {
      label: 'Ticket Holder',
      key: 'first_name',
      sort: sortByFn,
    },
    {
      label: 'Email',
      key: 'email',
      sort: sortByFn,
    },
    {
      label: 'Ticket Type',
      key: 'ticket_type',
      sort: sortByFn,
    },
  ])

  const fetchCheckInTickets = useCallback(async () => {
    const optionSortBy = sortBy.column
    const optionSortOrder = sortBy.asc ? 'asc' : 'desc'

    try {
      const data = await FETCH_EVENT_CHECKIN_TICKETS(event.id, '', optionSortBy, optionSortOrder)
      const ticketsArr = data || []

      setTickets(
        _map(ticketsArr, item => ({
          ...item,
          first_name: item.first_name + ' ' + item.last_name,
        })),
      )
    } catch (error) {
      showAxiosError(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy])

  useEffect(() => {
    fetchCheckInTickets()
  }, [fetchCheckInTickets, sortBy])

  const getCashlessPaymentsData = async rowData => {
    const setData = nfcData => {
      if (!nfcData?.transaction?.transactions) {
        return
      }

      const transactions = adaptData(nfcData.transaction.transactions)

      const ticketDetailRow = {
        id: rowData.id,
        type: 'detailRow',
        component: transactions.length ? (
          <SortableTable
            tableColumns={getTableColumns([
              {
                key: 'userName',
                value: 'User Name',
                normalizer: value => value || 'Unknown',
              },
              {
                key: 'balance',
                value: 'Balance',
                normalizer: value =>
                  currencyNormalizerCreator(nfcData?.event?.currency_mark)(
                    createFixedFloatNormalizer(2)(parseFloat(value / 100)),
                  ),
              },
            ])}
            data={transactions}
            enableCopyTable={true}
            actionsLabel=""
            detailsRows={transactions.map(transaction => ({
              id: transaction.id,
              type: 'detailRow',
              component: transaction.products.length ? (
                <SortableTable
                  data={transaction.products}
                  tableColumns={getTableColumns([
                    {
                      key: 'created_time',
                      label: 'Order Date',
                      normalizer: value => formatDate(value, DAY_DISPLAY_FORMAT, 'YYYY-MM-DD T HH:mm:ss'),
                    },
                    'product_name',
                    {
                      key: 'totalQuantity',
                      label: 'Quantity',
                    },
                    {
                      key: 'product_price',
                      label: 'Product price',
                      normalizer: value =>
                        currencyNormalizerCreator(nfcData?.event?.currency_mark)(
                          createFixedFloatNormalizer(2)(parseFloat(value / 100)),
                        ),
                    },
                    {
                      key: 'totalPrice',
                      label: 'Total price (incl. taxes)',
                      normalizer: value =>
                        currencyNormalizerCreator(nfcData?.event?.currency_mark)(
                          createFixedFloatNormalizer(2)(parseFloat(value / 100)),
                        ),
                    },
                  ])}
                  enableCopyTable={true}
                  actionsLabel=""
                  footbar={{
                    label: 'Total',
                    columns: [
                      {
                        key: 'totalPrice',
                        sourceKey: 'totalPrice',
                        normalizer: value =>
                          currencyNormalizerCreator(nfcData?.event?.currency_mark)(
                            createFixedFloatNormalizer(2)(parseFloat(value / 100)),
                          ),
                      },
                    ],
                  }}
                />
              ) : (
                <EmptyBar />
              ),
            }))}
          />
        ) : (
          <EmptyBar content={`We don't have enough data for ${rowData.credential_id || 'selected item'}`} />
        ),
      }

      setTicketDetailsRows([ticketDetailRow])
    }
    try {
      const res = await axios.get('https://admin-backend-hqtvybvuja-uc.a.run.app/api/new/landing/nfc', {
        params: { filter: { nfc_id: rowData.credential_id } },
      })

      setData(res?.data)
    } catch (error) {
      showAxiosError(error)

      const ticketDetailRow = {
        id: rowData.id,
        type: 'detailRow',
        component: (
          <EmptyBar content={`We don't have enough data for ${rowData.credential_id || 'selected item'}`} />
        ),
      }
      setTicketDetailsRows([ticketDetailRow])
    }
  }

  const adaptData = (data = []) => {
    const adaptedData = {}
    _forEach(data, transaction => {
      const userNameObj = transaction.items.find(tItem => tItem.dev_name)
      const existingProducts = adaptedData[transaction.user_id]
        ? [...adaptedData[transaction.user_id].products, ...transaction.items]
        : [...transaction.items]

      adaptedData[transaction.user_id] = {
        id: transaction.user_id,
        userName: adaptedData[transaction.user_id]?.userName || userNameObj?.dev_name,
        products: existingProducts,
        // Not sure that balance calculation is right
        balance: (adaptedData[transaction.user_id]?.balance || 0) + transaction.balance,
      }
    })

    const adaptedDataArr = Object.values(adaptedData)
    const updatedArr = adaptedDataArr.map(user => {
      const groupedProducts = _groupBy(user.products, product => [
        formatDate(product.created_time, 'YYYY-MM-DD', 'YYYY-MM-DD T HH:mm:ss'),
        product.product_name,
      ])

      const totalProducts = _map(groupedProducts, (values, key) => ({
        ...values[0],
        totalQuantity: values.length,
        totalPrice: values.length * values[0].total_price,
      }))

      return {
        ...user,
        products: totalProducts,
      }
    })

    return updatedArr
  }

  const handleRowExpand = async row => {
    if (row) {
      await getCashlessPaymentsData(row)
    }
  }

  if (isLoading) {
    return <LoadingBar />
  }

  if (!tickets?.length) {
    return <EmptyBar />
  }

  return (
    <div className="performance">
      {/* <div className="cashless">{displayCartSections()}</div> */}
      <SortableTable
        data={tickets}
        tableColumns={ticketsTableColumns}
        enableCopyTable={true}
        sortBy={sortBy}
        enableSort={true}
        disableMobileView={true}
        getDetailRowData={handleRowExpand}
        isAsyncDetails={true}
        detailsRows={ticketDetailsRows}
        setRowProps={row => ({ hideExpandIcon: !row.credential_id })}
      />
    </div>
  )
}

const mapStateToProps = state => ({
  event: get_event(state),
  checkInData: get_checkin(state),
  isLoading: state.loading.has('FETCH_EVENT_CHECKIN_TICKETS'),
})

export default connect(mapStateToProps, { FETCH_EVENT_CHECKIN_TICKETS })(CashlessPayments)
